import axios, { type AxiosResponse } from 'axios'
import { JwtPayload, jwtDecode } from 'jwt-decode'
import authConfig from 'src/configs/auth'
import { env } from '@/utils/env'

type Data = {
  access_token: string
  expires: number
  refresh_token: string
}

type RefreshTokenResponse = {
  data: Data
}

type Payload = {
  id: string
  role: string
  app_access: boolean
  admin_access: boolean
} & Pick<JwtPayload, 'iat' | 'exp' | 'iss'>

let refreshTokenPromise: Promise<string> | null = null

// Axios instance setup
const api = axios.create({
  baseURL: env('NEXT_PUBLIC_REST_API_URL'),
  timeout: 30000
})

// Helper function: Perform refresh token
async function refreshToken(): Promise<AxiosResponse<RefreshTokenResponse>> {
  const refreshToken = localStorage.getItem(authConfig.refreshTokenKeyName)

  if (!refreshToken) {
    throw new Error('Refresh token not found')
  }

  return axios.post<RefreshTokenResponse>(
    `${env('NEXT_PUBLIC_REST_API_URL')}/auth/refresh`,
    { refresh_token: refreshToken, mode: 'json' },
    { timeout: 30000 }
  )
}

// Initialize authentication on app load
async function initializeAuth(): Promise<void> {
  const token = typeof window !== 'undefined' && localStorage && localStorage.getItem(authConfig.accessTokenKeyName)

  if (token) {
    const decoded = jwtDecode<Payload>(token)
    const isTokenExpired = decoded.exp && Date.now() >= (decoded.exp - 60) * 1000

    if (isTokenExpired) {
      try {
        await handleRefreshToken()
      } catch (error) {
        console.error('Token refresh failed during initialization:', error)
        handleClearLocalStorage()
      }
    }
  }
}

// Handle refresh token with retry logic
async function handleRefreshToken(retries = 3): Promise<string> {
  if (!refreshTokenPromise) {
    // Retry inside this task; recursively awaiting the shared promise deadlocks its callers.
    refreshTokenPromise = (async () => {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const response = await refreshToken()
          const { access_token, refresh_token } = response.data.data
          localStorage.setItem(authConfig.accessTokenKeyName, access_token)
          localStorage.setItem(authConfig.refreshTokenKeyName, refresh_token)
          return access_token
        } catch (error: any) {
          if (error?.response?.status === 401 || attempt === retries - 1) {
            if (error?.response?.status === 401) handleClearLocalStorage()
            throw error
          }
        }
      }
      throw new Error('Failed to refresh token')
    })().finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

function handleClearLocalStorage() {
  localStorage.removeItem(authConfig.accessTokenKeyName)
  localStorage.removeItem(authConfig.refreshTokenKeyName)
  localStorage.clear()
}

// Request interceptor
api.interceptors.request.use(async config => {
  const token = localStorage.getItem(authConfig.accessTokenKeyName)

  if (config.url && ['/auth/login', '/auth/refresh', '/auth/logout'].includes(config.url)) {
    return config // Skip authentication for specific routes
  }

  if (!localStorage.getItem(authConfig.refreshTokenKeyName)) {
    handleClearLocalStorage() // If no refresh token, logout
    return config
  }

  if (token) {
    const decoded = jwtDecode<Payload>(token)
    const isTokenExpired = decoded.exp && Date.now() >= (decoded.exp - 60) * 1000

    if (!isTokenExpired) {
      config.headers.Authorization = `Bearer ${token}`
      return config
    }

    const newToken = await handleRefreshToken()
    config.headers.Authorization = `Bearer ${newToken}`
    return config
  } else {
    // No token, attempt to refresh
    try {
      const newToken = await handleRefreshToken()
      config.headers.Authorization = `Bearer ${newToken}`
    } catch (error) {
      handleClearLocalStorage()
    }
  }

  return config
})

// On app load, validate token and refresh if necessary
initializeAuth().catch(error => {
  console.error('Initialization failed:', error)
  if (typeof window !== 'undefined' && localStorage) {
    handleClearLocalStorage()
  }
})

// api.interceptors.request.use(
//   (req: AxiosRequestConfig) => {
//     const authService = env('NEXT_PUBLIC_AUTH_DEFAULT') || 'DATACORE'

//     /**
//      * auth service directus use content type app/json
//      */
//     if (authService === 'DIRECTUS') {
//       req.headers = { ...req.headers } as AxiosHeaders

//       // @ts-ignore
//       req.headers['Content-Type'] = 'application/json'
//     } else {
//       req.headers = { ...req.headers } as AxiosHeaders

//       // @ts-ignore
//       req.headers['Content-Type'] = 'application/x-www-form-urlencoded'
//     }

//     const storedToken = window.localStorage.getItem(authConfig.accessTokenKeyName)!

//     if (storedToken) {
//       req.headers = { ...req.headers } as AxiosHeaders

//       // @ts-ignore
//       req.headers['Authorization'] = 'Bearer ' + storedToken
//     }

//     return req
//   },
//   error => {
//     Promise.reject(error)
//   }
// )

// api.interceptors.response.use(
//   (response: AxiosResponse) => {
//     /**
//      * use ORCHARD
//      */
//     if (response.config.url === 'connect/token') {
//       window.localStorage.setItem(authConfig.accessTokenKeyName, response.data.data.access_token)
//     }

//     /**
//      * use DIRECTUS
//      */
//     if (response.config.url === 'auth/login') {
//       window.localStorage.setItem(authConfig.accessTokenKeyName, response.data.data.access_token)
//     }

//     return response
//   },
//   function (error) {
//     // const originalRequest = error.config

//     // if (error.response.status === 401 && !originalRequest._retry) {
//     //   originalRequest._retry = true
//     //   const refreshToken = localStorageService.getRefreshToken()
//     //   return axios
//     //     .post('/auth/token', {
//     //       refresh_token: refreshToken
//     //     })
//     //     .then(res => {
//     //       if (res.status === 201) {
//     //         localStorageService.setToken(res.data)
//     //         axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorageService.getAccessToken()
//     //         return axios(originalRequest)
//     //       }
//     //     })
//     // }
//     return Promise.reject(error)
//   }
// )

export default api
